import { Currency } from '@seapoint/server/enums';
import { TransactionView } from '../../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../../utils/get-amount-with-currency';

export type Metric = {
  label: string;
  value: string | number;
};

const hasMultipleCurrencies = (transactions: TransactionView[]): boolean => {
  if (transactions.length === 0) return false;
  const firstCurrency = transactions[0].currency;
  return transactions.some((transaction) => transaction.currency !== firstCurrency);
};

const getTransactionsCalculations = (transactions: TransactionView[]) => {
  const useBaseCurrency = hasMultipleCurrencies(transactions);

  const result = transactions.reduce(
    (acc, transaction) => {
      acc.numberOfTransactions++;
      const amount = useBaseCurrency ? transaction.baseCurrencyAmount : transaction.amount;

      if (transaction.isCredit) {
        acc.totalReceived += amount;
        acc.hasCredits = true;
      } else {
        acc.totalSpend += amount;
        acc.hasDebits = true;
      }

      return acc;
    },
    {
      numberOfTransactions: 0,
      totalSpend: 0,
      totalReceived: 0,
      hasCredits: false,
      hasDebits: false,
    },
  );

  // Calculate averages based on transaction type counts
  const creditCount = transactions.filter((t) => t.isCredit).length;
  const debitCount = transactions.filter((t) => !t.isCredit).length;
  const averageSpend = debitCount > 0 ? Number((result.totalSpend / debitCount).toFixed(2)) : 0;
  const averageReceived = creditCount > 0 ? Number((result.totalReceived / creditCount).toFixed(2)) : 0;

  return {
    ...result,
    averageSpend,
    averageReceived,
  };
};

const getFormattedTransactionsSummary = ({
  transactions,
  currency,
}: {
  transactions: TransactionView[];
  currency: Currency;
}) => {
  const useBaseCurrency = hasMultipleCurrencies(transactions);
  const effectiveCurrency = useBaseCurrency ? transactions[0]?.baseCurrency || currency : currency;
  const transactionsCalculations = getTransactionsCalculations(transactions);

  return {
    numberOfTransactions: transactionsCalculations.numberOfTransactions,
    totalSpend: getAmountWithCurrency({
      amount: transactionsCalculations.totalSpend,
      currency: effectiveCurrency,
      isAccountBalance: true,
    }),
    averageSpend: getAmountWithCurrency({
      amount: transactionsCalculations.averageSpend,
      currency: effectiveCurrency,
      isAccountBalance: true,
    }),
    totalReceived: getAmountWithCurrency({
      amount: transactionsCalculations.totalReceived,
      currency: effectiveCurrency,
      isAccountBalance: true,
    }),
    averageReceived: getAmountWithCurrency({
      amount: transactionsCalculations.averageReceived,
      currency: effectiveCurrency,
      isAccountBalance: true,
    }),
    hasCredits: transactionsCalculations.hasCredits,
    hasDebits: transactionsCalculations.hasDebits,
  };
};

export const getTransactionSummaryMetrics = (transactions: TransactionView[], currency: Currency) => {
  const { numberOfTransactions, totalSpend, averageSpend, totalReceived, averageReceived, hasCredits, hasDebits } =
    getFormattedTransactionsSummary({
      transactions,
      currency,
    });

  if (hasCredits && !hasDebits) {
    return [
      { label: 'Transactions', value: numberOfTransactions },
      { label: 'Total Received', value: totalReceived },
      { label: 'Average Received', value: averageReceived },
    ];
  }
  if (hasCredits && hasDebits) {
    return [
      { label: 'Transactions', value: numberOfTransactions },
      { label: 'Total Spend', value: totalSpend },
      { label: 'Total Received', value: totalReceived },
    ];
  }
  return [
    { label: 'Transactions', value: numberOfTransactions },
    { label: 'Total Spend', value: totalSpend },
    { label: 'Average Spend', value: averageSpend },
  ];
};
